import React, { useMemo } from 'react';
import { PatientProgress } from '../../types/domain';
import {
  progressFrequencyData,
  progressGlucoseData,
  progressHba1cData,
  progressInsulinData,
  progressSiteData
} from '../../lib/cohort';
import { formatLongDate, formatNumber } from '../../lib/monthlyReport';
import LazyTrendChart from './charts/LazyTrendChart';
import LazyCategoryChart from './charts/LazyCategoryChart';

interface PatientProgressPanelProps {
  progress: PatientProgress | null;
  loading: boolean;
  error?: string | null;
}

/**
 * The progress charts FR-14 asks for: glucose, total insulin, HbA1c, injection
 * sites and input frequency, for one patient over the dashboard's window.
 *
 * Charts render through the Lazy* wrappers, never the components or the
 * `charts/index.ts` barrel — this is an eager path, and importing either would
 * pull Recharts back into the main chunk with no error and no failing test.
 *
 * Each chart is omitted when it has nothing to draw rather than shown empty:
 * an axis with no line on it reads as "the child is at zero", which is the
 * opposite of "the child has not recorded".
 */
export const PatientProgressPanel: React.FC<PatientProgressPanelProps> = ({
  progress,
  loading,
  error
}) => {
  const glucoseSeries = useMemo(
    () => (progress ? progressGlucoseData(progress) : []),
    [progress]
  );
  const insulinSeries = useMemo(
    () => (progress ? progressInsulinData(progress) : []),
    [progress]
  );
  const hba1cSeries = useMemo(
    () => (progress ? progressHba1cData(progress) : []),
    [progress]
  );
  const siteSeries = useMemo(
    () => (progress ? progressSiteData(progress) : []),
    [progress]
  );
  const frequencySeries = useMemo(
    () => (progress ? progressFrequencyData(progress) : []),
    [progress]
  );

  if (loading) {
    return <div className="cohort-empty">Memuat grafik perkembangan...</div>;
  }

  if (error) {
    return (
      <div className="target-band-error" role="alert">
        {error}
      </div>
    );
  }

  if (!progress) {
    return null;
  }

  // `window` renamed: destructuring it here would shadow the global one.
  const { glucose, insulin, sites, frequency, clinical, window: range } = progress;
  const inTarget = glucose.readings
    ? Math.round((glucose.zones.normal / glucose.readings) * 100)
    : null;

  return (
    <div className="progress-panel">
      <div className="report-stats">
        <ProgressStat
          label="Cek gula darah"
          value={String(glucose.readings)}
          note={`rata-rata ${formatNumber(glucose.average)} mg/dL`}
        />
        <ProgressStat
          label="Di rentang aman"
          value={inTarget === null ? '–' : `${inTarget}%`}
          note={`${glucose.hypoCount} hipo · ${glucose.hyperCount} hiper`}
        />
        <ProgressStat
          label="Insulin"
          value={`${formatNumber(insulin.totalUnits)} u`}
          note={
            insulin.plannedDailyUnits > 0
              ? `${formatNumber(insulin.averageDailyUnits)} u/hari · jadwal ${formatNumber(
                  insulin.plannedDailyUnits
                )} u`
              : `${formatNumber(insulin.averageDailyUnits)} u/hari`
          }
        />
        <ProgressStat
          label="Frekuensi input"
          value={`${frequency.percent}%`}
          note={`${frequency.daysWithAnyEntry} dari ${frequency.daysInWindow} hari`}
        />
        <ProgressStat
          label="Titik suntik"
          value={String(sites.distinctPoints)}
          note={`${sites.injections} suntikan tercatat`}
        />
        <ProgressStat
          label="HbA1c terakhir"
          value={clinical.latestHba1c === null ? '–' : formatNumber(clinical.latestHba1c)}
          note={
            clinical.hba1cTrend.length
              ? `tes ${formatLongDate(
                  clinical.hba1cTrend[clinical.hba1cTrend.length - 1].date
                )}`
              : 'belum ada hasil tes'
          }
        />
      </div>

      {glucoseSeries.length ? (
        <div className="report-chart">
          <LazyTrendChart
            title="Gula darah harian"
            subtitle={`mg/dL · ${range.days} hari terakhir`}
            data={glucoseSeries}
            categoryKey="date"
            categoryLabel="Tanggal"
            series={[
              { key: 'average', label: 'Rata-rata' },
              { key: 'minimum', label: 'Terendah' },
              { key: 'maximum', label: 'Tertinggi' }
            ]}
            unit=" mg/dL"
            yDomain={[0, 'auto']}
          />
        </div>
      ) : null}

      {insulinSeries.length ? (
        <div className="report-chart">
          <LazyTrendChart
            title="Total insulin harian"
            subtitle={`unit · ${range.days} hari terakhir`}
            data={insulinSeries}
            categoryKey="date"
            categoryLabel="Tanggal"
            series={[{ key: 'units', label: 'Unit' }]}
            unit=" u"
            yDomain={[0, 'auto']}
          />
        </div>
      ) : null}

      {/* Two readings is the minimum a trend can mean anything from — the same
          rule FR-12 applies on the patient's own HbA1c chart. */}
      {hba1cSeries.length >= 2 ? (
        <div className="report-chart">
          <LazyTrendChart
            title="Tren HbA1c"
            subtitle="% · riwayat terakhir, bukan hanya rentang ini"
            data={hba1cSeries}
            categoryKey="date"
            categoryLabel="Tanggal tes"
            series={[{ key: 'hba1c', label: 'HbA1c' }]}
            unit="%"
          />
        </div>
      ) : null}

      {frequencySeries.length ? (
        <div className="report-chart">
          <LazyCategoryChart
            title="Frekuensi input per minggu"
            subtitle="hari tercatat dan jumlah catatan"
            data={frequencySeries}
            categoryKey="week"
            categoryLabel="Minggu mulai"
            series={[
              { key: 'days', label: 'Hari tercatat' },
              { key: 'entries', label: 'Jumlah catatan' }
            ]}
          />
        </div>
      ) : null}

      {siteSeries.length ? (
        <div className="report-chart">
          <LazyCategoryChart
            title="Lokasi suntik terbanyak"
            subtitle={`${sites.distinctPoints} titik berbeda dipakai`}
            data={siteSeries}
            categoryKey="site"
            categoryLabel="Titik suntik"
            series={[{ key: 'count', label: 'Jumlah suntikan' }]}
            layout="horizontal"
            height={Math.max(160, siteSeries.length * 34)}
          />
        </div>
      ) : null}

      {sites.lipodystrophyCount > 0 ? (
        <p className="cohort-note">
          {sites.lipodystrophyCount} catatan benjolan / penebalan kulit (lipodistrofi) pada
          rentang ini.
        </p>
      ) : null}

      {glucose.readings === 0 && insulin.injections === 0 ? (
        <p className="cohort-empty">
          Tidak ada catatan pada {range.days} hari terakhir.
        </p>
      ) : null}
    </div>
  );
};

const ProgressStat: React.FC<{ label: string; value: string; note: string }> = ({
  label,
  value,
  note
}) => (
  <div className="report-stat">
    <span className="report-stat-label">{label}</span>
    <span className="report-stat-value">{value}</span>
    <span className="report-stat-note">{note}</span>
  </div>
);

export default PatientProgressPanel;
